<template>
  <article
    v-if="active"
    class="featured-card featured-card-active"
  >
    <img
      :src="[item.image, fallbackImage] | mediaSrc"
      :alt="item.image ? item.imageAlt : fallbackImageAlt"
      class="featured-card-image"
    />
    <div class="featured-card-shade"></div>
    <div class="featured-card-content">
      <div class="featured-card-labels">
        <span v-if="showContentTypeLabel" class="featured-card-eyebrow">
          {{ contentTypeLabel }}
        </span>
        <router-link
          v-if="item.badge"
          :to="item.href"
          class="featured-card-badge"
        >
          {{ item.badge }}
        </router-link>
      </div>
      <h3>{{ item.title }}</h3>
      <p class="featured-card-excerpt">{{ item.excerpt }}</p>
      <p v-if="details.length" class="featured-card-details">
        <span v-for="detail in details" :key="detail">{{ detail }}</span>
      </p>
      <div class="featured-card-footer">
        <router-link :to="item.href" class="featured-card-cta">
          {{ ctaLabel }}
          <v-icon small>mdi-arrow-right</v-icon>
        </router-link>
        <span class="featured-card-position">{{ positionLabel }}</span>
      </div>
    </div>
  </article>

  <button
    v-else
    type="button"
    class="featured-card featured-card-supporting"
    :aria-label="`Show ${item.title}`"
    @click="$emit('activate')"
  >
    <img
      :src="[item.image, fallbackImage] | mediaSrc"
      :alt="item.image ? item.imageAlt : fallbackImageAlt"
      class="featured-card-image"
    />
    <div class="featured-card-shade"></div>
    <div class="featured-card-content featured-card-content-supporting">
      <span class="featured-card-eyebrow">{{ contentTypeLabel }}</span>
      <h3>{{ item.title }}</h3>
    </div>
  </button>
</template>

<script>
import { mediaRef } from '@/services/mediaAssets';

const fallbackImage = mediaRef('adventures/reckon-way-featured-fallback.png');

const CTA_LABELS = {
  story: 'Read the Story',
  retreat: 'Explore the Trip',
  conference: 'Explore the Trip',
  'reckon-ball': 'Relive the Night',
  tradition: 'See the Tradition',
  'local-event': 'See the Tradition',
};

const CONTENT_TYPE_LABELS = {
  story: 'Story',
  retreat: 'Corporate Retreat',
  conference: 'Conference',
  'reckon-ball': 'Reckon Ball',
  tradition: 'Tradition',
  'local-event': 'Local Event',
};

export default {
  name: 'FeaturedContentCard',
  data: () => ({
    fallbackImage,
    fallbackImageAlt: 'A Reckon hero overlooking a mountain landscape at sunset',
  }),
  props: {
    item: { type: Object, required: true },
    active: { type: Boolean, default: false },
    positionLabel: { type: String, default: '' },
  },
  computed: {
    ctaLabel() {
      return CTA_LABELS[this.item.contentType] || 'Explore';
    },
    contentTypeLabel() {
      return CONTENT_TYPE_LABELS[this.item.contentType] || this.item.contentType;
    },
    showContentTypeLabel() {
      return !this.item.title.toLowerCase().startsWith(this.contentTypeLabel.toLowerCase());
    },
    details() {
      return [this.item.location, this.item.eventDate].filter(Boolean);
    },
  },
};
</script>

<style scoped>
.featured-card {
  background: #111;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 16px;
  color: #fff;
  display: block;
  height: 100%;
  overflow: hidden;
  padding: 0;
  position: relative;
  text-align: left;
  width: 100%;
}

.featured-card-image {
  display: block;
  height: 100%;
  inset: 0;
  object-fit: cover;
  object-position: center;
  position: absolute;
  width: 100%;
}

.featured-card-shade {
  background: linear-gradient(0deg, rgba(0, 0, 0, 0.78) 0%, rgba(0, 0, 0, 0.36) 42%, rgba(0, 0, 0, 0.02) 78%);
  inset: 0;
  position: absolute;
}

.featured-card-content {
  bottom: 0;
  left: 0;
  padding: clamp(24px, 4vw, 42px);
  position: absolute;
  right: 0;
  z-index: 1;
}

.featured-card-labels,
.featured-card-footer,
.featured-card-details {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
}

.featured-card-labels {
  gap: 10px;
}

.featured-card-eyebrow,
.featured-card-badge {
  font-family: "Oswald", sans-serif;
  font-size: 0.76rem;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.featured-card-eyebrow {
  color: #ff4c4c;
}

.featured-card-badge {
  background: #e23232;
  border-radius: 999px;
  color: #fff;
  padding: 5px 9px;
  text-decoration: none;
}

.featured-card-badge:hover,
.featured-card-badge:focus-visible {
  background: #ff4545;
  outline: 2px solid rgba(255, 255, 255, 0.82);
  outline-offset: 2px;
}

h3 {
  color: #fff;
  font-family: "Oswald", sans-serif;
  font-size: clamp(2rem, 4vw, 3.4rem);
  line-height: 1;
  margin: 10px 0 12px;
  text-shadow: 0 2px 16px rgba(0, 0, 0, 0.72);
}

.featured-card-excerpt {
  color: #e2e2e2;
  font-size: 1rem;
  line-height: 1.5;
  margin: 0 0 12px;
  max-width: 720px;
}

.featured-card-details {
  color: #c8c8c8;
  font-size: 0.82rem;
  gap: 8px 18px;
  margin: 0 0 20px;
}

.featured-card-details span + span::before {
  color: #e23232;
  content: "•";
  margin-right: 18px;
}

.featured-card-footer {
  gap: 18px;
  justify-content: space-between;
}

.featured-card-cta {
  align-items: center;
  background: #e23232;
  border-radius: 999px;
  color: #fff;
  display: inline-flex;
  font-family: "Oswald", sans-serif;
  font-size: 0.82rem;
  font-weight: 900;
  gap: 8px;
  padding: 10px 17px;
  text-decoration: none;
  text-transform: uppercase;
}

.featured-card-cta:hover,
.featured-card-cta:focus-visible {
  background: #ff4545;
  outline: 3px solid rgba(255, 255, 255, 0.82);
  outline-offset: 3px;
}

.featured-card-cta .v-icon {
  color: inherit;
}

.featured-card-position {
  color: #d5d5d5;
  font-size: 0.78rem;
  font-weight: 800;
}

.featured-card-supporting {
  cursor: pointer;
  font: inherit;
}

.featured-card-supporting .featured-card-image {
  filter: brightness(0.9);
  transform: scale(1.01);
}

.featured-card-content-supporting {
  padding: 24px;
}

.featured-card-content-supporting h3 {
  font-size: clamp(1.35rem, 2.2vw, 2rem);
  line-height: 1.08;
  margin-bottom: 0;
}

.featured-card-supporting:hover,
.featured-card-supporting:focus-visible {
  border-color: #e23232;
  outline: 3px solid rgba(226, 50, 50, 0.42);
  outline-offset: 3px;
}

@media (max-width: 700px) {
  .featured-card-content {
    padding: 22px;
  }

  h3 {
    font-size: clamp(2rem, 10vw, 2.8rem);
  }

  .featured-card-excerpt {
    display: -webkit-box;
    font-size: 0.92rem;
    overflow: hidden;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
  }

  .featured-card-details {
    margin-bottom: 16px;
  }
}
</style>
